import {
  DatabaseContainerType,
  getDatabaseContainerType,
  useAnalytics as getAnalyticsClient,
} from '@databases/analytics';
import { BrokenDatabase, Database, DatabaseFieldType } from '@databases/types/database';
import { DatabaseChangeType } from '@databases/types/redux';
import {
  addChangeToChangeset,
  DatabaseChange,
  DatabaseChangeset,
  doPageDeletions,
} from '@databases/utils/database/changesets';
import { isBrokenDatabase } from '@databases/utils/database/utils';
import {
  addDatabaseToList,
  loadDatabaseByContentId,
} from '@databases/utils/redux/databases/actions';
import { selectDatabaseByContentIdOptional } from '@databases/utils/redux/databases/selectors';
import { deserializeError } from '@databases/utils/utils';
import { debounce } from 'lodash-es';
import { IntlShape } from 'react-intl-next';
import { AnyAction } from 'redux';
import { ThunkAction } from 'redux-thunk';

import { DefaultRootState } from '../redux';

import changesetsSlice from './slice';

type Dispatcher<Result> = ThunkAction<Result, DefaultRootState, void, AnyAction>;

const sendDatabaseCellContentHasBeenModifiedEventDebounced = debounce(
  async (
    contentId: string,
    containerType: DatabaseContainerType,
    fieldType: DatabaseFieldType
  ) => {
    const client = getAnalyticsClient();
    if (client !== undefined) {
      await client.sendDatabaseCellContentHasBeenModifiedEvent({
        contentId: contentId,
        databaseContainer: containerType,
        fieldType,
      });
    }
  },
  1000
);

const applyChangetoCollabDatabase =
  (database: Database, changeset: DatabaseChangeset): Dispatcher<void> =>
  (dispatch, getState) => {
    const collabManager = getState().collab.byContentId[database.contentId];
    collabManager.applyChangeset(changeset, getState());
  };

/**
 * Adds a drafted database update. The drafted updates can be applied by calling applyDraftedDatabaseUpdates().
 * A preview of the database draft can be retrieved before the update is saved using the database selectors.
 */
export const addChangeToDraft =
  (contentId: string, scope: string, change: DatabaseChange): Dispatcher<void> =>
  (dispatch, getState) => {
    if (change.type === DatabaseChangeType.SET_VALUE && change.change?.type) {
      sendDatabaseCellContentHasBeenModifiedEventDebounced(
        contentId,
        getDatabaseContainerType(),
        change.change.type
      );
    }

    const database = selectDatabaseByContentIdOptional(getState(), { contentId });
    if (!database) {
      throw new Error(`Database ${contentId} not found.`);
    } else if (isBrokenDatabase(database)) {
      throw deserializeError(database.error);
    }

    if (scope === 'main') {
      const changeset = addChangeToChangeset([], change);

      dispatch(applyChangetoCollabDatabase(database, changeset));
    } else {
      dispatch(
        changesetsSlice.actions.addChangesToDraft({
          contentId,
          scope,
          changes: [change],
        })
      );
    }
  };

/**
 * Applies the drafted database updates and saves a new version of the database. Registers an unload handler
 * while the updates are running.
 */
export const saveDraft =
  (contentId: string, scope: string, intl: IntlShape): Dispatcher<Promise<void>> =>
  async (dispatch, getState) => {
    const state = getState();
    const changeset = state.changesets.scopes[scope]?.draftedChangesets[contentId] ?? [];
    const savingChangeset =
      state.changesets.scopes[scope]?.savingChangeset[contentId] ?? [];

    if (savingChangeset.length > 0 || changeset.length === 0) {
      return;
    }

    await dispatch(async () => {
      let database: Database | BrokenDatabase | undefined = undefined;

      database = await dispatch(loadDatabaseByContentId(contentId, intl));
      if (!database) {
        throw new Error(`Database ${contentId} not found.`);
      } else if (isBrokenDatabase(database)) {
        throw deserializeError(database.error);
      }

      try {
        await doPageDeletions(database, changeset, { dispatch, getState });

        await dispatch(applyChangetoCollabDatabase(database, changeset));
      } catch (error: any) {
        // An error occurred somewhere while running the database update. This could be while running the side effects or
        // while updating one of the CCEOs. To recover, we move the running updates back to the drafted updates, add
        // any updates that the successful side effects have created and reload the database in case one of its CCEOs
        // was successfully updated.
        const databaseNow = await dispatch(
          loadDatabaseByContentId(contentId, intl, { forceReload: true })
        );
        dispatch(addDatabaseToList(databaseNow));

        throw error;
      }
    });
  };

/**
 * Remove any drafted updates without applying them.
 */
export const resetDraft = (contentId: string, scope: string): AnyAction =>
  changesetsSlice.actions.setDraftedChangeset({ contentId, scope, changeset: [] });

export const setDraftedChangeset =
  (contentId: string, scope: string, changeset: DatabaseChangeset): Dispatcher<void> =>
  (dispatch, getState) => {
    const database = selectDatabaseByContentIdOptional(getState(), { contentId });
    if (!database) {
      throw new Error(`Database ${contentId} not found.`);
    } else if (isBrokenDatabase(database)) {
      throw deserializeError(database.error);
    }

    if (scope === 'main') {
      dispatch(applyChangetoCollabDatabase(database, changeset));
    } else {
      dispatch(
        changesetsSlice.actions.setDraftedChangeset({ contentId, scope, changeset })
      );
    }
  };
